import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Admin from "./components/Admin";
import FeedList from "./components/FeedList";
import AddFeedSource from "./components/AddFeedSouce";
import { useAuth } from "../../../context/AuthContext";

function App() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [isLoggedIn, setIsLoggedIn] = useState(!!user);
  const [feeds, setFeeds] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [predefinedFeeds, setPredefinedFeeds] = useState([]);
  const [showAddFeedSource, setShowAddFeedSource] = useState(false);

  // Styles
  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "20px",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "15px 0",
      marginBottom: "20px",
    },
    title: {
      fontSize: "24px",
      fontWeight: "600",
      color: "#2c3e50",
      margin: "0",
    },
    logoutButton: {
      backgroundColor: "#e74c3c",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
      fontSize: "14px",
      transition: "background-color 0.2s",
    },
    buttonContainer: {
      display: "flex",
      gap: "10px",
      marginBottom: "20px",
      flexWrap: "wrap",
    },
    actionButton: {
      backgroundColor: "#3498db",
      color: "white",
      border: "none",
      padding: "10px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
      fontSize: "14px",
      transition: "background-color 0.2s",
    },
    predefinedFeedsContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      marginBottom: "30px",
      padding: "15px",
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
    feedButton: {
      backgroundColor: "#f1f8fe",
      color: "#3498db",
      border: "1px solid #d1e6fa",
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
      fontSize: "14px",
      transition: "all 0.2s",
    },
    predefinedFeedsTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#2c3e50",
      marginBottom: "10px",
      width: "100%",
    },
    errorMessage: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "12px 20px",
      borderRadius: "4px",
      marginBottom: "20px",
      border: "1px solid #f5c6cb",
    },
    loadingMessage: {
      textAlign: "center",
      padding: "40px 0",
      color: "#6c757d",
      fontSize: "18px",
    },
    sectionDivider: {
      height: "1px",
      backgroundColor: "#e9ecef",
      margin: "20px 0",
      width: "100%",
    },
  };

  const fetchPredefinedFeeds = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/rss/api/predefined-feeds`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPredefinedFeeds(data);
    } catch (err) {
      console.error("Error fetching predefined feeds:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) setIsLoggedIn(true);
  }, [user]);

  useEffect(() => {
    if (isLoggedIn) fetchPredefinedFeeds();
  }, [isLoggedIn]);

  const addFeed = (feedData, replace = false) => {
    // feedData can be either a URL string (from Admin component) or an object with name and url (from predefined feeds)
    const feedObject =
      typeof feedData === "string"
        ? { url: feedData, name: `Custom Feed ${feeds.length + 1}` }
        : feedData;

    if (replace) {
      // Replace all existing feeds with the new one (for predefined feeds)
      setFeeds([feedObject]);
    } else {
      // Add to existing feeds (for custom feeds from Admin component)
      setFeeds([...feeds, feedObject]);
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsLoggedIn(false);
    setPredefinedFeeds([]);
    setError(null);
    navigate("/");
  };

  const handleToggleAddFeedSource = () => {
    setShowAddFeedSource((prevState) => !prevState);
  };

  const handleSourceAdded = (newSource) => {
    // Add the new source to the predefined feeds list
    setPredefinedFeeds((prevFeeds) => [...prevFeeds, newSource]);
    // Optionally refresh from server to ensure consistency
    fetchPredefinedFeeds();
  };

  const handleCloseFeedSource = () => {
    setShowAddFeedSource(false);
  };

  if (loading && predefinedFeeds.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingMessage}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="rss-container">
      <style>{`
        @media (max-width: 1024px) {
          .rss-container { padding: 16px; }
          .rss-header { flex-direction: column; align-items: flex-start; gap: 12px; }
          .rss-header-actions { width: 100%; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
          .rss-header-actions button { flex: 1; min-width: 140px; }
          .rss-buttons { gap: 8px; }
        }
        @media (max-width: 768px) {
          .rss-container { padding: 12px; }
          .rss-button-row { flex-direction: column; width: 100%; }
          .rss-button-row button { width: 100%; }
          .rss-predefined { padding: 12px; }
          .rss-predefined button { width: 100%; text-align: left; }
        }
        @media (max-width: 480px) {
          .rss-container { padding: 10px; }
          .rss-title { font-size: 20px; }
        }
      `}</style>
      <div style={styles.header} className="rss-header">
        <h1 style={styles.title} className="rss-title">RSS Feed Management</h1>
        <div style={{ display: "flex", gap: "10px" }} className="rss-header-actions">
          <button
            style={{
              ...styles.logoutButton,
              backgroundColor: "#6c757d",
            }}
            onClick={() => navigate("/DsportAdmin")}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#5a6268";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#6c757d";
            }}
          >
            Back
          </button>
          <button
            style={styles.logoutButton}
            onClick={handleLogout}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#c0392b";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#e74c3c";
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.errorMessage}>
          Error: {error}
          <button
            onClick={fetchPredefinedFeeds}
            style={{ ...styles.actionButton, marginLeft: "10px" }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={styles.buttonContainer} className="rss-button-row">
        <button
          style={styles.actionButton}
          onClick={() => navigate("/saved-feeds")}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#2980b9";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#3498db";
          }}
        >
          View Saved Feeds in DB
        </button>
        <button
          style={{
            ...styles.actionButton,
            backgroundColor: showAddFeedSource ? "#e67e22" : "#3498db",
          }}
          onClick={handleToggleAddFeedSource}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = showAddFeedSource
              ? "#d35400"
              : "#2980b9";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = showAddFeedSource
              ? "#e67e22"
              : "#3498db";
          }}
        >
          {showAddFeedSource ? "Hide Feed Source Panel" : "Add Feed Source"}
        </button>
      </div>

      {showAddFeedSource && (
        <AddFeedSource
          onSourceAdded={handleSourceAdded}
          onClose={handleCloseFeedSource}
        />
      )}

      {predefinedFeeds.length > 0 && (
        <div style={styles.predefinedFeedsContainer} className="rss-predefined">
          <div style={styles.predefinedFeedsTitle}>Predefined Feed Sources</div>
          {predefinedFeeds.map((feed, index) => (
            <button
              key={feed.id || index}
              style={styles.feedButton}
              onClick={() => addFeed({ url: feed.url, name: feed.name }, true)}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#d1e6fa";
                e.currentTarget.style.borderColor = "#3498db";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#f1f8fe";
                e.currentTarget.style.borderColor = "#d1e6fa";
              }}
            >
              {feed.name}
            </button>
          ))}
        </div>
      )}

      <Admin addFeed={addFeed} />
      <div style={styles.sectionDivider}></div>
      <FeedList feeds={feeds} />
    </div>
  );
}

export default App;
